'use client'

import { Moon, SunDim } from 'lucide-react'
import { useRef, type ReactNode } from 'react'
import { flushSync } from 'react-dom'

import { cn } from '@repo/ui/lib/utils'

type AnimatedThemeTogglerProps = {
  className?: string
  isDark?: boolean
  onToggle?: () => void
  duration?: number
  ariaLabel?: string
  title?: string
  children?: ReactNode
}

type DocumentWithViewTransition = Document & {
  startViewTransition?: (callback: () => void) => { ready: Promise<void> }
}

export function AnimatedThemeToggler({
  className,
  isDark = false,
  onToggle,
  duration = 700,
  ariaLabel,
  title,
  children,
}: AnimatedThemeTogglerProps) {
  const buttonRef = useRef<HTMLButtonElement>(null)

  const changeTheme = async () => {
    if (!buttonRef.current || !onToggle) return

    const doc = document as DocumentWithViewTransition

    if (typeof doc.startViewTransition !== 'function') {
      onToggle()
      return
    }

    const transition = doc.startViewTransition(() => {
      flushSync(() => {
        onToggle()
      })
    })

    await transition.ready

    const { top, left, width, height } =
      buttonRef.current.getBoundingClientRect()
    const y = top + height / 2
    const x = left + width / 2
    const right = window.innerWidth - left
    const bottom = window.innerHeight - top
    const maxRad = Math.hypot(Math.max(left, right), Math.max(top, bottom))

    document.documentElement.animate(
      {
        clipPath: [
          `circle(0px at ${x}px ${y}px)`,
          `circle(${maxRad}px at ${x}px ${y}px)`,
        ],
      },
      {
        duration,
        easing: 'ease-in-out',
        pseudoElement: '::view-transition-new(root)',
      }
    )
  }

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={changeTheme}
      aria-label={ariaLabel}
      title={title}
      className={cn(className)}
    >
      {children ?? (isDark ? <SunDim /> : <Moon />)}
    </button>
  )
}
